"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@/app/_components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/app/_components/ui/input-otp";
import { SignupFormValues } from "@/app/_lib/schemas/auth";
import { cn } from "@/app/_lib/utils";
import { UseFormReturn } from "react-hook-form";
import { verifyToken, sendVerificationToken } from "../lib/actions";
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "@/app/_hooks/use-toast";
import Link from "next/link";

export default function Token({
  form,
  onNextStep,
}: {
  form: UseFormReturn<SignupFormValues>;
  onNextStep: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const onSubmit = async (data: SignupFormValues) => {
    if (!data.code || data.code.length < 4) {
      form.setError("code", { message: "Enter a 4-digit code" });
      return;
    }

    try {
      setLoading(true);
      const response = await verifyToken(data.email, data.code);
      if (response && response.success) {
        toast({
          title: "Success",
          description: "Email verified successfully.",
        });
        onNextStep();
      } else {
        form.setError("code", { message: response?.error || "Invalid code" });
        toast({
          variant: "destructive",
          title: "Error",
          description: response?.error || "Invalid verification code.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: (error as any).message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      setResending(true);
      const email = form.getValues("email");
      const response = await sendVerificationToken(email);

      if (response && response.success) {
        setResendCooldown(60);
        toast({
          title: "Code Resent",
          variant:"default",
          description: `A new verification code was sent to ${email}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response?.error || "Failed to resend code",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: (error as any).message || "Something went wrong",
      });
    } finally {
      setResending(false);
    }
  };

  return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4 w-full"
        >
          <div className="px-4 py-3 bg-accent rounded-xl text-sm text-foreground border border-border truncate">
            {form.getValues("email")}
          </div>

          <FormField
            name="code"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <div className="flex flex-col justify-center items-center gap-3">
                    <InputOTP
                      className="flex flex-row"
                      maxLength={4}
                      value={field.value}
                      onChange={field.onChange}
                    >
                      {[0, 1, 2, 3].map((i) => (
                        <InputOTPGroup key={i}>
                          <InputOTPSlot
                            index={i}
                            render={({ char, isActive }) => (
                              <span
                                className={cn(
                                  "transition",
                                  !char && "text-muted-foreground"
                                )}
                              >
                                {char || (isActive ? "" : "#")}
                              </span>
                            )}
                          />
                        </InputOTPGroup>
                      ))}
                    </InputOTP>
                    {fieldState.error && (
                      <p className="text-sm text-red-500">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <button
            type="button"
            onClick={handleResend}
            disabled={resending || resendCooldown > 0}
            className={cn(
              "text-muted-foreground underline cursor-pointer",
              (resending || resendCooldown > 0) && "opacity-70 cursor-not-allowed"
            )}
          >
            {resending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Resending...
              </span>
            ) : resendCooldown > 0 ? (
              `Resend in ${resendCooldown}s`
            ) : (
              "Resend Code"
            )}
          </button>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Verifying…
              </>
            ) : (
              "Verify & continue"
            )}
          </button>

        
        </form>
      </Form>
  );
}
